import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Typography, TextField, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Card, CardContent, Grid,
} from '@mui/material';
import { FindInPage, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { fetchReconciliation } from '../../../store/slices/ledgerSlice';

const SEVERITY_COLOR = {
  CRITICAL: 'error',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'default',
};

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN') : '-';
const fmt = (p) => p != null
  ? `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
  : '—';

const ReconciliationReport = () => {
  const dispatch = useDispatch();
  const { reconciliation, loading, error } = useSelector(s => s.ledger);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const handleFetch = () => {
    if (date) dispatch(fetchReconciliation(date));
  };

  const run = reconciliation?.run;
  const breaks = reconciliation?.breaks || [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Daily reconciliation compares billing_events against ledger_entries to detect
        missing, mismatched, or partial journal entries.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
        <TextField
          label="Date (YYYY-MM-DD)"
          size="small"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          startIcon={<FindInPage />}
          onClick={handleFetch}
          disabled={!date || loading.reconciliation}
        >
          {loading.reconciliation ? 'Loading…' : 'Fetch Report'}
        </Button>
      </Box>

      {loading.reconciliation && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error.reconciliation && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No reconciliation report found for {date}. Reports are generated nightly at 02:00 IST.
        </Alert>
      )}

      {run && (
        <Box>
          {/* Summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    {run.status === 'clean' || run.break_count === 0
                      ? <CheckCircle sx={{ color: 'success.main' }} />
                      : <ErrorIcon sx={{ color: 'error.main' }} />
                    }
                    <Typography fontWeight="bold" color={run.break_count === 0 ? 'success.main' : 'error.main'}>
                      {run.status?.toUpperCase() ?? (run.break_count === 0 ? 'CLEAN' : 'BREAKS')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Breaks Found</Typography>
                  <Typography variant="h5" fontWeight="bold" color={run.break_count > 0 ? 'error.main' : 'inherit'}>
                    {run.break_count ?? breaks.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Events Checked</Typography>
                  <Typography variant="h5" fontWeight="bold">{run.events_checked ?? '—'}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Run At</Typography>
                  <Typography variant="body2" fontWeight="bold">{fmtDate(run.run_at)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Breaks table */}
          {breaks.length === 0 ? (
            <Alert severity="success">No reconciliation breaks found for {date}. Ledger is clean.</Alert>
          ) : (
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Break Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Billing Event</TableCell>
                    <TableCell align="right">Expected</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breaks.map((b, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {b.break_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={b.severity} size="small" color={SEVERITY_COLOR[b.severity] || 'default'} />
                      </TableCell>
                      <TableCell>{b.billing_event_id ?? '-'}</TableCell>
                      <TableCell align="right">{fmt(b.expected_amount_paise)}</TableCell>
                      <TableCell align="right">{fmt(b.actual_amount_paise)}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{b.description}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReconciliationReport;
